import moment from 'moment'
import { formatDate } from './helper'
import storage, { PORTAL_TOKENS, tokenKeyFor } from '../services/storage'

/**
 * Format a number into a currency string (USD format).
 * @param {number} number - The number to format as currency.
 * @returns {string} - The formatted currency string.
 */
export function formatCurrency(number) {
  return `$${number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

/**
 * Number formatter for currency (USD).
 * @type {Intl.NumberFormat}
 */
export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

/**
 * Formatter for US Dollar currency without decimal places.
 * Uses Intl.NumberFormat with style 'currency' and currency 'USD'.
 */
export const formatterInt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/**
 * Number formatter for currency (EUR) with 2 decimal places.
 * @type {Intl.NumberFormat}
 */
export const euroFormatter = new Intl.NumberFormat('en-IE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
})

/**
 * Formatter for Euro currency without decimal places.
 * Uses Intl.NumberFormat with style 'currency' and currency 'EUR'.
 */
export const euroFormatterInt = new Intl.NumberFormat('en-IE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/**
 * Check if a string contains whitespace.
 * @param {string} stringVal - The string to check.
 * @returns {boolean} - True if the string contains whitespace, false otherwise.
 */
export const stringValPatternValidation = stringVal => {
  return /\s/g.test(stringVal)
}

/**
 * Get a number from a currency-formatted string.
 * @param {string} currencyAmount - The currency-formatted string.
 * @returns {number|null} - The numeric value or null if not a valid string.
 */
export function getNumberFromCurrency(currencyAmount) {
  if (Number.isInteger(currencyAmount)) {
    return currencyAmount
  }

  if (currencyAmount === null) {
    return null
  }

  if (!(typeof currencyAmount === 'string')) {
    throw new Error('Type of currency amount should be a string or integer.')
  }

  const numberStringFromCurrencyAmount = currencyAmount.replace(/,/g, '').replace(/\$/g, '')
  if (isValidIntegerString(numberStringFromCurrencyAmount)) {
    return parseInt(numberStringFromCurrencyAmount)
  }

  return null
}

/**
 * Check if a string is a valid USA zip code.
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string is a valid USA zip code, false otherwise.
 */
export const isUSAZipCode = str => {
  return /^\d{5}(-\d{4})?$/.test(str)
}

/**
 * Validate a phone number string.
 * @param {string} str - The phone number string to validate.
 * @returns {boolean} - True if the string is a valid phone number, false otherwise.
 */
export const validatePhoneNumber = str => {
  return /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/.test(str)
}

/**
 * Format a file name from a string.
 * @param {string} string - The input string containing a file name.
 * @returns {string} - The extracted file name.
 */
export function formatFileToName(string) {
  const sections = string.split('-')
  return sections[sections.length - 1]
}

/**
 * Check the number of elements with 'Female Founder' tag in an array of objects.
 * @param {Array} value - The array of objects to check.
 * @returns {number} - The count of elements with 'Female Founder' tag.
 */
export const checkFemaleStyle = value => {
  return value?.filter(key => key?.tags?.includes('Female Founder'))?.length
}

/**
 * Check if a given object is a valid integer string.
 * @param {any} obj - The object to check.
 * @returns {boolean} - True if the object is a valid integer string, false otherwise.
 */
function isValidIntegerString(obj) {
  if (isNaN(obj)) {
    return false
  }

  const floorNumber = Math.floor(Number(obj))
  return floorNumber !== Infinity && String(obj) === obj
}

/**
 * Set the authentication token in the local storage.
 * @param {string|null} token - The authentication token. Set to null to remove the token.
 */
export function setTokenInLocalStorage(type, token) {
  if (token === null || token === undefined) {
    Object.values(PORTAL_TOKENS).forEach(descriptor => storage.remove(descriptor))
    return
  }

  const descriptor = tokenKeyFor(type)
  if (descriptor) {
    storage.set(descriptor, token)
  }
}

/**
 * Get the authentication token from local storage.
 * @returns {string|null} - The authentication token or null if not present.
 */
export function getTokenInLocaleStorage(type) {
  const descriptor = tokenKeyFor(type)
  // Unknown/absent type returns undefined, matching the previous behaviour.
  return descriptor ? storage.get(descriptor) : undefined
}

/**
 * Get the identity object from an authentication token.
 * @param {string} token - The authentication token.
 * @throws {Error} - Throws an error if the token is null or undefined.
 * @returns {object} - The identity object extracted from the token.
 */
function getIdentityFromToken(token) {
  if (token === null || token === undefined) {
    throw new Error('Cannot get identity from token if token is null or undefined.')
  }

  const base64Payload = token.split('.')[1]

  const payloadJson = JSON.parse(window.atob(base64Payload))

  return payloadJson.identity
}

/**
 * Get the email address from an authentication token.
 * @param {string} token - The authentication token.
 * @returns {string} - The email address extracted from the token.
 */
export function getEmailFromToken(token) {
  return getIdentityFromToken(token).email
}

/**
 * Get the user type from an authentication token.
 * @param {string} token - The authentication token.
 * @returns {string} - The user type extracted from the token.
 */
export function getUserTypeFromToken(token) {
  return getIdentityFromToken(token).model
}

/**
 * Require a non-empty value and throw an error if it is empty.
 * @param {any} obj - The value to check for emptiness.
 * @param {string} message - The error message to throw if the value is empty.
 * @returns {any} - The non-empty value.
 * @throws {Error} - Throws an error if the value is empty.
 */
export function requireNonEmpty(obj, message) {
  if (obj === null || obj === undefined || obj === '') {
    throw new Error(message)
  }

  return obj
}

/**
 * Get the file name from a URL.
 * @param {string} url - The URL containing the file name.
 * @returns {string} - The extracted file name.
 */
export function getFileNameFromUrl(url) {
  //https://exitfund-client-pdf-bucket.s3-us-west-1.amazonaws.com/62ff18ec485efc30e4041796-copy-1654689972.pdf
  const a = url?.split('/')?.[url?.split('/')?.length - 1]
  let b = a?.split('-')
  b.shift()
  b = b.join('-')
  return b
}

// Array of valid incorporation types.
export const INCORPORATION_TYPE = ['C Corporation', 'LLC', 'PBC', 'S Corporation', 'Other']

/**
 * Generate a public URL link based on the environment.
 * @param {string} link - The environment link.
 * @param {string} startupName - The name of the startup.
 * @param {string} invite_code - The invite code for the startup.
 * @returns {string} - The public URL link.
 */
export const publicURLLink = (link, startupName, invite_code) => {
  switch (link) {
    case 'localhost:3000':
      return link + `/startup/${startupName}/${invite_code}`
    case 'stg.exitfund.com':
      return `https://stg.exitfund.com/startup/${startupName}/${invite_code}`
    case 'exitfund.com':
      return `https://www.exitfund.com/startup/${startupName}/${invite_code}`
    default:
      return ''
  }
}

/**
 * Generate a public Monthly Updates URL link based on the environment.
 * @param {string} link - The environment link.
 * @param {string} startupName - The name of the startup.
 * @returns {string} - The public Monthly Updates URL link.
 */
export const publicMonthlyUpdatesURLLink = (link, startupName, personalInviteLink) => {
  switch (link) {
    case 'localhost:3000':
      return `http://${link}/startup/${personalInviteLink}/${startupName}/monthly-updates`
    case 'stg.exitfund.com':
      return `https://stg.exitfund.com/startup/${personalInviteLink}/${startupName}/monthly-updates`
    case 'exitfund.com':
      return `https://www.exitfund.com/startup/${personalInviteLink}/${startupName}/monthly-updates`
    default:
      return ''
  }
}

/**
 * Get a startup name with spaces replaced by underscores.
 * @param {string} name - The original startup name.
 * @returns {string} - The startup name with spaces replaced by underscores.
 */
export const getStartupName = name => {
  return name?.replaceAll(' ', '_')
}

/**
 * Adjust the setup cost array by replacing 'MIN' and 'MAX' values.
 * @param {Array} array - The array to be adjusted.
 * @param {number} min - The minimum value to replace 'MIN'.
 * @param {number} max - The maximum value to replace 'MAX'.
 * @returns {Array} - The adjusted array.
 */

export function getSetupCostArray(array, min, max) {
  if (array?.length) {
    let data = array.map(item => {
      if (item.min === 'MIN') {
        return { ...item, min: 0 }
      } else if (item.max === 'MAX') {
        return { ...item, max: max }
      } else {
        return item
      }
    })
    return data
  } else {
    return []
  }
}

/**
 * Check if a string contains only letters and spaces.
 * @param {string} value - The string to check.
 * @returns {boolean} - True if the string contains only letters and spaces, false otherwise.
 */
export const nameRegxCheck = value => {
  let newRegx = /^[A-Za-z\s-]*$/
  if (!newRegx.test(value)) {
    return false
  }
  return true
}

/**
 * Format a number as a percentage or display 'N/A'.
 * @param {number} value - The numeric value to format.
 * @returns {string} - The formatted percentage or 'N/A'.
 */
export const NumberValidCheck = value => {
  return value == 0 ? `${value}%` : !value ? 'N/A' : `${value}%`
}

/**
 * Format a number as currency or display 'N/A'.
 * @param {number} value - The numeric value to format.
 * @returns {string} - The formatted currency or 'N/A'.
 */
export const DollerNumberValidCheck = value => {
  return value == 0 ? formatter.format(value) : !value ? 'N/A' : formatter.format(value)
}

export const DollerNumberValidCheckInt = value => {
  return value == 0 ? formatterInt.format(value) : !value ? 'N/A' : formatterInt.format(value)
}

/**
 * Display the closing date in the 'MM-DD-YYYY' format.
 * @param {Date} date - The date to be formatted.
 * @returns {string} - The formatted date string.
 */

export const displayClosingdate = date => {
  return moment(date).utc(true).local().format('MM-DD-YYYY')
}

/**
 * Convert a local date to UTC format.
 * @param {Date} date - The local date to be converted.
 * @returns {string} - The date in UTC format.
 */
export const convertLocalToUTC = date => {
  return moment(moment(formatDate(date)).format('YYYY-MM-DDT00:00:00'))
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss')
}

export const NewCountryList = [
  {
    name: 'South Georgia',
    flag: 'https://flagcdn.com/gs.svg',
  },
  {
    name: 'Grenada',
    flag: 'https://flagcdn.com/gd.svg',
  },
  {
    name: 'Switzerland',
    flag: 'https://flagcdn.com/ch.svg',
  },
  {
    name: 'Sierra Leone',
    flag: 'https://flagcdn.com/sl.svg',
  },
  {
    name: 'Hungary',
    flag: 'https://flagcdn.com/hu.svg',
  },
  {
    name: 'Taiwan',
    flag: 'https://flagcdn.com/tw.svg',
  },
  {
    name: 'Wallis and Futuna',
    flag: 'https://flagcdn.com/wf.svg',
  },
  {
    name: 'Barbados',
    flag: 'https://flagcdn.com/bb.svg',
  },
  {
    name: 'Pitcairn Islands',
    flag: 'https://flagcdn.com/pn.svg',
  },
  {
    name: 'Ivory Coast',
    flag: 'https://flagcdn.com/ci.svg',
  },
  {
    name: 'Tunisia',
    flag: 'https://flagcdn.com/tn.svg',
  },
  {
    name: 'Italy',
    flag: 'https://flagcdn.com/it.svg',
  },
  {
    name: 'Benin',
    flag: 'https://flagcdn.com/bj.svg',
  },
  {
    name: 'Indonesia',
    flag: 'https://flagcdn.com/id.svg',
  },
  {
    name: 'Cape Verde',
    flag: 'https://flagcdn.com/cv.svg',
  },
  {
    name: 'Saint Kitts and Nevis',
    flag: 'https://flagcdn.com/kn.svg',
  },
  {
    name: 'Laos',
    flag: 'https://flagcdn.com/la.svg',
  },
  {
    name: 'Caribbean Netherlands',
    flag: 'https://flagcdn.com/bq.svg',
  },
  {
    name: 'Uganda',
    flag: 'https://flagcdn.com/ug.svg',
  },
  {
    name: 'Andorra',
    flag: 'https://flagcdn.com/ad.svg',
  },
  {
    name: 'Burundi',
    flag: 'https://flagcdn.com/bi.svg',
  },
  {
    name: 'South Africa',
    flag: 'https://flagcdn.com/za.svg',
  },
  {
    name: 'France',
    flag: 'https://flagcdn.com/fr.svg',
  },
  {
    name: 'Libya',
    flag: 'https://flagcdn.com/ly.svg',
  },
  {
    name: 'Mexico',
    flag: 'https://flagcdn.com/mx.svg',
  },
  {
    name: 'Gabon',
    flag: 'https://flagcdn.com/ga.svg',
  },
  {
    name: 'Northern Mariana Islands',
    flag: 'https://flagcdn.com/mp.svg',
  },
  {
    name: 'North Macedonia',
    flag: 'https://flagcdn.com/mk.svg',
  },
  {
    name: 'China',
    flag: 'https://flagcdn.com/cn.svg',
  },
  {
    name: 'Yemen',
    flag: 'https://flagcdn.com/ye.svg',
  },
  {
    name: 'Saint Barthélemy',
    flag: 'https://flagcdn.com/bl.svg',
  },
  {
    name: 'Guernsey',
    flag: 'https://flagcdn.com/gg.svg',
  },
  {
    name: 'Solomon Islands',
    flag: 'https://flagcdn.com/sb.svg',
  },
  {
    name: 'Svalbard and Jan Mayen',
    flag: 'https://flagcdn.com/sj.svg',
  },
  {
    name: 'Faroe Islands',
    flag: 'https://flagcdn.com/fo.svg',
  },
  {
    name: 'Uzbekistan',
    flag: 'https://flagcdn.com/uz.svg',
  },
  {
    name: 'Egypt',
    flag: 'https://flagcdn.com/eg.svg',
  },
  {
    name: 'Senegal',
    flag: 'https://flagcdn.com/sn.svg',
  },
  {
    name: 'Sri Lanka',
    flag: 'https://flagcdn.com/lk.svg',
  },
  {
    name: 'Palestine',
    flag: 'https://flagcdn.com/ps.svg',
  },
  {
    name: 'Bangladesh',
    flag: 'https://flagcdn.com/bd.svg',
  },
  {
    name: 'Peru',
    flag: 'https://flagcdn.com/pe.svg',
  },
  {
    name: 'Singapore',
    flag: 'https://flagcdn.com/sg.svg',
  },
  {
    name: 'Turkey',
    flag: 'https://flagcdn.com/tr.svg',
  },
  {
    name: 'Afghanistan',
    flag: 'https://upload.wikimedia.org/wikipedia/commons/5/5c/Flag_of_the_Taliban.svg',
  },
  {
    name: 'Aruba',
    flag: 'https://flagcdn.com/aw.svg',
  },
  {
    name: 'Cook Islands',
    flag: 'https://flagcdn.com/ck.svg',
  },
  {
    name: 'United Kingdom',
    flag: 'https://flagcdn.com/gb.svg',
  },
  {
    name: 'Zambia',
    flag: 'https://flagcdn.com/zm.svg',
  },
  {
    name: 'Finland',
    flag: 'https://flagcdn.com/fi.svg',
  },
  {
    name: 'Niger',
    flag: 'https://flagcdn.com/ne.svg',
  },
  {
    name: 'Christmas Island',
    flag: 'https://flagcdn.com/cx.svg',
  },
  {
    name: 'Tokelau',
    flag: 'https://flagcdn.com/tk.svg',
  },
  {
    name: 'Guinea-Bissau',
    flag: 'https://flagcdn.com/gw.svg',
  },
  {
    name: 'Azerbaijan',
    flag: 'https://flagcdn.com/az.svg',
  },
  {
    name: 'Réunion',
    flag: 'https://flagcdn.com/re.svg',
  },
  {
    name: 'Djibouti',
    flag: 'https://flagcdn.com/dj.svg',
  },
  {
    name: 'North Korea',
    flag: 'https://flagcdn.com/kp.svg',
  },
  {
    name: 'Mauritius',
    flag: 'https://flagcdn.com/mu.svg',
  },
  {
    name: 'Montserrat',
    flag: 'https://flagcdn.com/ms.svg',
  },
  {
    name: 'United States Virgin Islands',
    flag: 'https://flagcdn.com/vi.svg',
  },
  {
    name: 'Colombia',
    flag: 'https://flagcdn.com/co.svg',
  },
  {
    name: 'Greece',
    flag: 'https://flagcdn.com/gr.svg',
  },
  {
    name: 'Croatia',
    flag: 'https://flagcdn.com/hr.svg',
  },
  {
    name: 'Morocco',
    flag: 'https://flagcdn.com/ma.svg',
  },
  {
    name: 'Algeria',
    flag: 'https://flagcdn.com/dz.svg',
  },
  {
    name: 'Antarctica',
    flag: 'https://flagcdn.com/aq.svg',
  },
  {
    name: 'Netherlands',
    flag: 'https://flagcdn.com/nl.svg',
  },
  {
    name: 'Sudan',
    flag: 'https://flagcdn.com/sd.svg',
  },
  {
    name: 'Fiji',
    flag: 'https://flagcdn.com/fj.svg',
  },
  {
    name: 'Liechtenstein',
    flag: 'https://flagcdn.com/li.svg',
  },
  {
    name: 'Nepal',
    flag: 'https://flagcdn.com/np.svg',
  },
  {
    name: 'Puerto Rico',
    flag: 'https://flagcdn.com/pr.svg',
  },
  {
    name: 'Georgia',
    flag: 'https://flagcdn.com/ge.svg',
  },
  {
    name: 'Pakistan',
    flag: 'https://flagcdn.com/pk.svg',
  },
  {
    name: 'Monaco',
    flag: 'https://flagcdn.com/mc.svg',
  },
  {
    name: 'Botswana',
    flag: 'https://flagcdn.com/bw.svg',
  },
  {
    name: 'Lebanon',
    flag: 'https://flagcdn.com/lb.svg',
  },
  {
    name: 'Papua New Guinea',
    flag: 'https://flagcdn.com/pg.svg',
  },
  {
    name: 'Mayotte',
    flag: 'https://flagcdn.com/yt.svg',
  },
  {
    name: 'Dominican Republic',
    flag: 'https://flagcdn.com/do.svg',
  },
  {
    name: 'Norfolk Island',
    flag: 'https://flagcdn.com/nf.svg',
  },
  {
    name: 'Bouvet Island',
    flag: 'https://flagcdn.com/bv.svg',
  },
  {
    name: 'Qatar',
    flag: 'https://flagcdn.com/qa.svg',
  },
  {
    name: 'Madagascar',
    flag: 'https://flagcdn.com/mg.svg',
  },
  {
    name: 'India',
    flag: 'https://flagcdn.com/in.svg',
  },
  {
    name: 'Syria',
    flag: 'https://flagcdn.com/sy.svg',
  },
  {
    name: 'Montenegro',
    flag: 'https://flagcdn.com/me.svg',
  },
  {
    name: 'Eswatini',
    flag: 'https://flagcdn.com/sz.svg',
  },
  {
    name: 'Paraguay',
    flag: 'https://flagcdn.com/py.svg',
  },
  {
    name: 'El Salvador',
    flag: 'https://flagcdn.com/sv.svg',
  },
  {
    name: 'Ukraine',
    flag: 'https://flagcdn.com/ua.svg',
  },
  {
    name: 'Isle of Man',
    flag: 'https://flagcdn.com/im.svg',
  },
  {
    name: 'Namibia',
    flag: 'https://flagcdn.com/na.svg',
  },
  {
    name: 'United Arab Emirates',
    flag: 'https://flagcdn.com/ae.svg',
  },
  {
    name: 'Bulgaria',
    flag: 'https://flagcdn.com/bg.svg',
  },
  {
    name: 'Greenland',
    flag: 'https://flagcdn.com/gl.svg',
  },
  {
    name: 'Germany',
    flag: 'https://flagcdn.com/de.svg',
  },
  {
    name: 'Cambodia',
    flag: 'https://flagcdn.com/kh.svg',
  },
  {
    name: 'Iraq',
    flag: 'https://flagcdn.com/iq.svg',
  },
  {
    name: 'French Southern and Antarctic Lands',
    flag: 'https://flagcdn.com/tf.svg',
  },
  {
    name: 'Sweden',
    flag: 'https://flagcdn.com/se.svg',
  },
  {
    name: 'Cuba',
    flag: 'https://flagcdn.com/cu.svg',
  },
  {
    name: 'Kyrgyzstan',
    flag: 'https://flagcdn.com/kg.svg',
  },
  {
    name: 'Russia',
    flag: 'https://flagcdn.com/ru.svg',
  },
  {
    name: 'Malaysia',
    flag: 'https://flagcdn.com/my.svg',
  },
  {
    name: 'São Tomé and Príncipe',
    flag: 'https://flagcdn.com/st.svg',
  },
  {
    name: 'Cyprus',
    flag: 'https://flagcdn.com/cy.svg',
  },
  {
    name: 'Canada',
    flag: 'https://flagcdn.com/ca.svg',
  },
  {
    name: 'Malawi',
    flag: 'https://flagcdn.com/mw.svg',
  },
  {
    name: 'Saudi Arabia',
    flag: 'https://flagcdn.com/sa.svg',
  },
  {
    name: 'Bosnia and Herzegovina',
    flag: 'https://flagcdn.com/ba.svg',
  },
  {
    name: 'Ethiopia',
    flag: 'https://flagcdn.com/et.svg',
  },
  {
    name: 'Spain',
    flag: 'https://flagcdn.com/es.svg',
  },
  {
    name: 'Slovenia',
    flag: 'https://flagcdn.com/si.svg',
  },
  {
    name: 'Oman',
    flag: 'https://flagcdn.com/om.svg',
  },
  {
    name: 'Saint Pierre and Miquelon',
    flag: 'https://flagcdn.com/pm.svg',
  },
  {
    name: 'Macau',
    flag: 'https://flagcdn.com/mo.svg',
  },
  {
    name: 'San Marino',
    flag: 'https://flagcdn.com/sm.svg',
  },
  {
    name: 'Lesotho',
    flag: 'https://flagcdn.com/ls.svg',
  },
  {
    name: 'Marshall Islands',
    flag: 'https://flagcdn.com/mh.svg',
  },
  {
    name: 'Sint Maarten',
    flag: 'https://flagcdn.com/sx.svg',
  },
  {
    name: 'Iceland',
    flag: 'https://flagcdn.com/is.svg',
  },
  {
    name: 'Luxembourg',
    flag: 'https://flagcdn.com/lu.svg',
  },
  {
    name: 'Argentina',
    flag: 'https://flagcdn.com/ar.svg',
  },
  {
    name: 'Turks and Caicos Islands',
    flag: 'https://flagcdn.com/tc.svg',
  },
  {
    name: 'Nauru',
    flag: 'https://flagcdn.com/nr.svg',
  },
  {
    name: 'Cocos (Keeling) Islands',
    flag: 'https://flagcdn.com/cc.svg',
  },
  {
    name: 'Western Sahara',
    flag: 'https://flagcdn.com/eh.svg',
  },
  {
    name: 'Dominica',
    flag: 'https://flagcdn.com/dm.svg',
  },
  {
    name: 'Costa Rica',
    flag: 'https://flagcdn.com/cr.svg',
  },
  {
    name: 'Australia',
    flag: 'https://flagcdn.com/au.svg',
  },
  {
    name: 'Thailand',
    flag: 'https://flagcdn.com/th.svg',
  },
  {
    name: 'Haiti',
    flag: 'https://flagcdn.com/ht.svg',
  },
  {
    name: 'Tuvalu',
    flag: 'https://flagcdn.com/tv.svg',
  },
  {
    name: 'Honduras',
    flag: 'https://flagcdn.com/hn.svg',
  },
  {
    name: 'Equatorial Guinea',
    flag: 'https://flagcdn.com/gq.svg',
  },
  {
    name: 'Saint Lucia',
    flag: 'https://flagcdn.com/lc.svg',
  },
  {
    name: 'French Polynesia',
    flag: 'https://flagcdn.com/pf.svg',
  },
  {
    name: 'Belarus',
    flag: 'https://flagcdn.com/by.svg',
  },
  {
    name: 'Latvia',
    flag: 'https://flagcdn.com/lv.svg',
  },
  {
    name: 'Palau',
    flag: 'https://flagcdn.com/pw.svg',
  },
  {
    name: 'Guadeloupe',
    flag: 'https://flagcdn.com/gp.svg',
  },
  {
    name: 'Philippines',
    flag: 'https://flagcdn.com/ph.svg',
  },
  {
    name: 'Gibraltar',
    flag: 'https://flagcdn.com/gi.svg',
  },
  {
    name: 'Denmark',
    flag: 'https://flagcdn.com/dk.svg',
  },
  {
    name: 'Cameroon',
    flag: 'https://flagcdn.com/cm.svg',
  },
  {
    name: 'Guinea',
    flag: 'https://flagcdn.com/gn.svg',
  },
  {
    name: 'Bahrain',
    flag: 'https://flagcdn.com/bh.svg',
  },
  {
    name: 'Suriname',
    flag: 'https://flagcdn.com/sr.svg',
  },
  {
    name: 'DR Congo',
    flag: 'https://flagcdn.com/cd.svg',
  },
  {
    name: 'Somalia',
    flag: 'https://flagcdn.com/so.svg',
  },
  {
    name: 'Czechia',
    flag: 'https://flagcdn.com/cz.svg',
  },
  {
    name: 'New Caledonia',
    flag: 'https://flagcdn.com/nc.svg',
  },
  {
    name: 'Vanuatu',
    flag: 'https://flagcdn.com/vu.svg',
  },
  {
    name: 'Saint Helena, Ascension and Tristan da Cunha',
    flag: 'https://flagcdn.com/sh.svg',
  },
  {
    name: 'Togo',
    flag: 'https://flagcdn.com/tg.svg',
  },
  {
    name: 'British Virgin Islands',
    flag: 'https://flagcdn.com/vg.svg',
  },
  {
    name: 'Kenya',
    flag: 'https://flagcdn.com/ke.svg',
  },
  {
    name: 'Niue',
    flag: 'https://flagcdn.com/nu.svg',
  },
  {
    name: 'Heard Island and McDonald Islands',
    flag: 'https://flagcdn.com/hm.svg',
  },
  {
    name: 'Rwanda',
    flag: 'https://flagcdn.com/rw.svg',
  },
  {
    name: 'Estonia',
    flag: 'https://flagcdn.com/ee.svg',
  },
  {
    name: 'Romania',
    flag: 'https://flagcdn.com/ro.svg',
  },
  {
    name: 'Trinidad and Tobago',
    flag: 'https://flagcdn.com/tt.svg',
  },
  {
    name: 'Guyana',
    flag: 'https://flagcdn.com/gy.svg',
  },
  {
    name: 'Timor-Leste',
    flag: 'https://flagcdn.com/tl.svg',
  },
  {
    name: 'Vietnam',
    flag: 'https://flagcdn.com/vn.svg',
  },
  {
    name: 'Uruguay',
    flag: 'https://flagcdn.com/uy.svg',
  },
  {
    name: 'Vatican City',
    flag: 'https://flagcdn.com/va.svg',
  },
  {
    name: 'Hong Kong',
    flag: 'https://flagcdn.com/hk.svg',
  },
  {
    name: 'Austria',
    flag: 'https://flagcdn.com/at.svg',
  },
  {
    name: 'Antigua and Barbuda',
    flag: 'https://flagcdn.com/ag.svg',
  },
  {
    name: 'Turkmenistan',
    flag: 'https://flagcdn.com/tm.svg',
  },
  {
    name: 'Mozambique',
    flag: 'https://flagcdn.com/mz.svg',
  },
  {
    name: 'Panama',
    flag: 'https://flagcdn.com/pa.svg',
  },
  {
    name: 'Micronesia',
    flag: 'https://flagcdn.com/fm.svg',
  },
  {
    name: 'Ireland',
    flag: 'https://flagcdn.com/ie.svg',
  },
  {
    name: 'Curaçao',
    flag: 'https://flagcdn.com/cw.svg',
  },
  {
    name: 'French Guiana',
    flag: 'https://flagcdn.com/gf.svg',
  },
  {
    name: 'Norway',
    flag: 'https://flagcdn.com/no.svg',
  },
  {
    name: 'Åland Islands',
    flag: 'https://flagcdn.com/ax.svg',
  },
  {
    name: 'Central African Republic',
    flag: 'https://flagcdn.com/cf.svg',
  },
  {
    name: 'Burkina Faso',
    flag: 'https://flagcdn.com/bf.svg',
  },
  {
    name: 'Eritrea',
    flag: 'https://flagcdn.com/er.svg',
  },
  {
    name: 'Tanzania',
    flag: 'https://flagcdn.com/tz.svg',
  },
  {
    name: 'South Korea',
    flag: 'https://flagcdn.com/kr.svg',
  },
  {
    name: 'Jordan',
    flag: 'https://flagcdn.com/jo.svg',
  },
  {
    name: 'Mauritania',
    flag: 'https://flagcdn.com/mr.svg',
  },
  {
    name: 'Lithuania',
    flag: 'https://flagcdn.com/lt.svg',
  },
  {
    name: 'United States Minor Outlying Islands',
    flag: 'https://flagcdn.com/um.svg',
  },
  {
    name: 'Slovakia',
    flag: 'https://flagcdn.com/sk.svg',
  },
  {
    name: 'Angola',
    flag: 'https://flagcdn.com/ao.svg',
  },
  {
    name: 'Kazakhstan',
    flag: 'https://flagcdn.com/kz.svg',
  },
  {
    name: 'Moldova',
    flag: 'https://flagcdn.com/md.svg',
  },
  {
    name: 'Mali',
    flag: 'https://flagcdn.com/ml.svg',
  },
  {
    name: 'Falkland Islands',
    flag: 'https://flagcdn.com/fk.svg',
  },
  {
    name: 'Armenia',
    flag: 'https://flagcdn.com/am.svg',
  },
  {
    name: 'Samoa',
    flag: 'https://flagcdn.com/ws.svg',
  },
  {
    name: 'Jersey',
    flag: 'https://flagcdn.com/je.svg',
  },
  {
    name: 'Japan',
    flag: 'https://flagcdn.com/jp.svg',
  },
  {
    name: 'Bolivia',
    flag: 'https://flagcdn.com/bo.svg',
  },
  {
    name: 'Chile',
    flag: 'https://flagcdn.com/cl.svg',
  },
  {
    name: 'United States',
    flag: 'https://flagcdn.com/us.svg',
  },
  {
    name: 'Saint Vincent and the Grenadines',
    flag: 'https://flagcdn.com/vc.svg',
  },
  {
    name: 'Bermuda',
    flag: 'https://flagcdn.com/bm.svg',
  },
  {
    name: 'Seychelles',
    flag: 'https://flagcdn.com/sc.svg',
  },
  {
    name: 'British Indian Ocean Territory',
    flag: 'https://flagcdn.com/io.svg',
  },
  {
    name: 'Guatemala',
    flag: 'https://flagcdn.com/gt.svg',
  },
  {
    name: 'Ecuador',
    flag: 'https://flagcdn.com/ec.svg',
  },
  {
    name: 'Martinique',
    flag: 'https://flagcdn.com/mq.svg',
  },
  {
    name: 'Tajikistan',
    flag: 'https://flagcdn.com/tj.svg',
  },
  {
    name: 'Malta',
    flag: 'https://flagcdn.com/mt.svg',
  },
  {
    name: 'Gambia',
    flag: 'https://flagcdn.com/gm.svg',
  },
  {
    name: 'Nigeria',
    flag: 'https://flagcdn.com/ng.svg',
  },
  {
    name: 'Bahamas',
    flag: 'https://flagcdn.com/bs.svg',
  },
  {
    name: 'Kosovo',
    flag: 'https://flagcdn.com/xk.svg',
  },
  {
    name: 'Kuwait',
    flag: 'https://flagcdn.com/kw.svg',
  },
  {
    name: 'Maldives',
    flag: 'https://flagcdn.com/mv.svg',
  },
  {
    name: 'South Sudan',
    flag: 'https://flagcdn.com/ss.svg',
  },
  {
    name: 'Iran',
    flag: 'https://flagcdn.com/ir.svg',
  },
  {
    name: 'Albania',
    flag: 'https://flagcdn.com/al.svg',
  },
  {
    name: 'Brazil',
    flag: 'https://flagcdn.com/br.svg',
  },
  {
    name: 'Serbia',
    flag: 'https://flagcdn.com/rs.svg',
  },
  {
    name: 'Belize',
    flag: 'https://flagcdn.com/bz.svg',
  },
  {
    name: 'Myanmar',
    flag: 'https://flagcdn.com/mm.svg',
  },
  {
    name: 'Bhutan',
    flag: 'https://flagcdn.com/bt.svg',
  },
  {
    name: 'Venezuela',
    flag: 'https://flagcdn.com/ve.svg',
  },
  {
    name: 'Liberia',
    flag: 'https://flagcdn.com/lr.svg',
  },
  {
    name: 'Jamaica',
    flag: 'https://flagcdn.com/jm.svg',
  },
  {
    name: 'Poland',
    flag: 'https://flagcdn.com/pl.svg',
  },
  {
    name: 'Cayman Islands',
    flag: 'https://flagcdn.com/ky.svg',
  },
  {
    name: 'Brunei',
    flag: 'https://flagcdn.com/bn.svg',
  },
  {
    name: 'Comoros',
    flag: 'https://flagcdn.com/km.svg',
  },
  {
    name: 'Guam',
    flag: 'https://flagcdn.com/gu.svg',
  },
  {
    name: 'Tonga',
    flag: 'https://flagcdn.com/to.svg',
  },
  {
    name: 'Kiribati',
    flag: 'https://flagcdn.com/ki.svg',
  },
  {
    name: 'Ghana',
    flag: 'https://flagcdn.com/gh.svg',
  },
  {
    name: 'Chad',
    flag: 'https://flagcdn.com/td.svg',
  },
  {
    name: 'Zimbabwe',
    flag: 'https://flagcdn.com/zw.svg',
  },
  {
    name: 'Saint Martin',
    flag: 'https://flagcdn.com/mf.svg',
  },
  {
    name: 'Mongolia',
    flag: 'https://flagcdn.com/mn.svg',
  },
  {
    name: 'Portugal',
    flag: 'https://flagcdn.com/pt.svg',
  },
  {
    name: 'American Samoa',
    flag: 'https://flagcdn.com/as.svg',
  },
  {
    name: 'Republic of the Congo',
    flag: 'https://flagcdn.com/cg.svg',
  },
  {
    name: 'Belgium',
    flag: 'https://flagcdn.com/be.svg',
  },
  {
    name: 'Israel',
    flag: 'https://flagcdn.com/il.svg',
  },
  {
    name: 'New Zealand',
    flag: 'https://flagcdn.com/nz.svg',
  },
  {
    name: 'Nicaragua',
    flag: 'https://flagcdn.com/ni.svg',
  },
  {
    name: 'Anguilla',
    flag: 'https://flagcdn.com/ai.svg',
  },
]

export const dummyRequest = ({ file, onSuccess }) => {
  setTimeout(() => {
    onSuccess('ok')
  }, 0)
}
